/**
 * Logger abstraction. Call sites use this — they don't import Sentry /
 * Datadog / PostHog directly. That way swapping the backend is a one-file
 * change and call sites stay vendor-agnostic.
 *
 * Defaults to console with level filtering driven by VITE_LOG_LEVEL.
 * In production wire `setLogger()` to your real telemetry sink during boot.
 */

type Level = 'debug' | 'info' | 'warn' | 'error';
const ORDER: Record<Level, number> = { debug: 10, info: 20, warn: 30, error: 40 };

export interface Logger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, error?: unknown, context?: Record<string, unknown>): void;
}

// Redact obvious PII before any context hits a log sink. Add fields as
// your domain grows. Keep this small — better to under-log than leak.
const PII_KEYS = new Set(['email', 'phone', 'password', 'token', 'authorization', 'ssn', 'address']);
function redact(ctx: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!ctx) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(ctx)) {
    out[k] = PII_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : v;
  }
  return out;
}

function consoleLogger(min: Level): Logger {
  const should = (l: Level) => ORDER[l] >= ORDER[min];
  return {
    debug: (m, c) => should('debug') && console.debug(m, redact(c) ?? ''),
    info: (m, c) => should('info') && console.info(m, redact(c) ?? ''),
    warn: (m, c) => should('warn') && console.warn(m, redact(c) ?? ''),
    error: (m, e, c) => should('error') && console.error(m, e, redact(c) ?? ''),
  };
}

let active: Logger = consoleLogger((import.meta.env.VITE_LOG_LEVEL ?? 'info') as Level);

export const logger: Logger = {
  debug: (m, c) => active.debug(m, c),
  info: (m, c) => active.info(m, c),
  warn: (m, c) => active.warn(m, c),
  error: (m, e, c) => active.error(m, e, c),
};

/** Swap the logger implementation at boot. Pass a Sentry/Datadog adapter here. */
export function setLogger(impl: Logger): void {
  active = impl;
}
